// =============================================
// File: src/components/activity/SpirometryActivityDetails.jsx
// Purpose: Thin component reusing generic useRRTrend and spirometry hooks
// =============================================
import React from "react";
import { useTranslation } from "react-i18next";
import { ResultCard } from "../result/ResultCard";
import { ChartBlock } from "../result/ChartBlock";
import MetricLineChart from "../charts/MetricLineChart";
import MetricTrendChart from "../charts/MetricTrendChart";
import RadialGauge from "../charts/RadialGauge";
import { SmallChartBlock } from "./SmallChartBlock";
import Spacer from "../ui/Spacer";
import { LockOverlay } from "../ui/LockOverlay";

import { useRRTrend } from "../../hooks/useRRTrend";
import { useSpirometrySummary } from "../../hooks/useSpirometrySummary";
import { useSpirometryFlowDataset } from "../../hooks/useSpirometryFlowDataset";

export default function SpirometryActivityDetails({
  uid,
  baseName,
  date,
  data,
  locks = {},
  redemptionCTA,
  downloadURL,
}) {
  const { t } = useTranslation();

  const { subtitle, ratioPct, rr, lungAge, lungFitnessPct, guidance } =
    useSpirometrySummary({ date, data });

  const {
    loading: loadingRRPrev,
    labels: rrLabels,
    values: rrValues,
    minMax: rrMinMax,
  } = useRRTrend({
    uid,
    baseName,
    date,
    windowDays: 30,
    limit: undefined,
    tag: "breatheultra",
    // Use the real value stored on items/docs
    variant: "RecordingForcedSpirometry",
    // or (either/or works if you wire it through):
    repo: "pdx-recording-forced-spirometry",
  });

  console.log(rrValues, rrMinMax, rrLabels, date);

  const { flowDataset, xMax } = useSpirometryFlowDataset({ data, t });

  return (
    <div className="w-full max-w-lg flex flex-col gap-4">
      <ResultCard
        title={t("activity.spirometry.title", {
          defaultValue: "Lung Fitness & Age Test",
        })}
        subtitle={subtitle}
      />

      {/* Lung Fitness */}
      <SmallChartBlock
        title={t("metrics.lungFitness", { defaultValue: "Lung Fitness Score" })}
        infoKey="info.lungFitnessScore"
        value={lungFitnessPct}
      >
        <RadialGauge
          value={lungFitnessPct ?? 0}
          max={100}
          size={96}
          thickness={8}
          format={(v) => `${Math.min(100, Math.max(0, Math.round(v)))}`}
        />
        <LockOverlay
          show={!!locks?.lungFitness}
          to={redemptionCTA?.to}
          onClick={redemptionCTA?.onClick}
          ctaKey="activity.lockedCta"
          ctaDefault={redemptionCTA?.label ?? "Unlock Results"}
          ariaKey="activity.lockedCtaAria"
          ariaDefault={
            redemptionCTA?.ariaLabel ?? "Go to checkout to unlock results"
          }
        />
      </SmallChartBlock>
      <Spacer />

      {/* Lung Age */}
      {/* <SmallChartBlock
        title={t("metrics.lungAge.name", { defaultValue: "Lung Age" })}
        infoKey="info.lungAge"
        value={lungAge}
        unit={t("metrics.lungAge.unit", { defaultValue: "yrs" })}
      >
        <RadialGauge
          value={lungAge ?? 0}
          max={100}
          size={96}
          thickness={8}
          format={(v) => `${Math.min(100, Math.max(0, Math.round(v)))}`}
        />
        <LockOverlay
          show={!!locks?.lungAge}
          to={redemptionCTA?.to}
          onClick={redemptionCTA?.onClick}
          ctaKey="activity.lockedCta"
          ctaDefault={redemptionCTA?.label ?? "Unlock Results"}
          ariaKey="activity.lockedCtaAria"
          ariaDefault={
            redemptionCTA?.ariaLabel ?? "Go to checkout to unlock results"
          }
        />
      </SmallChartBlock>
      <Spacer /> */}

      {/* RR Trend */}
      <SmallChartBlock
        title={t("metrics.respiratoryRate.name", {
          defaultValue: "Respiratory Rate",
        })}
        infoKey="info.respiratoryRate"
        value={rr}
        unit={t("metrics.respiratoryRate.unit", { defaultValue: "bpm" })}
      >
        <MetricTrendChart
          height={128}
          labels={rrLabels}
          values={rrValues}
          minMax={rrMinMax}
          lineLabel={t("metrics.rr", { defaultValue: "Respiratory Rate" })}
          loading={loadingRRPrev}
          yTickStep={1}
          yDecimals={0}
          showMinMaxBars={true}
          minMaxWidth={8}
        />
      </SmallChartBlock>
      <Spacer />

      {/* Breath Trace */}
      <ChartBlock
        title={t("activity.spirometry.chartTitle", {
          defaultValue: "Breathing Trace",
        })}
        infoKey="info.breathingTrace"
      >
        <MetricLineChart
          height={180}
          datasets={flowDataset ? [flowDataset] : []}
          preset="none"
          showLegend={false}
          showTooltip={false}
          xType="linear"
          xMin={0}
          xMax={xMax}
          xTickStep={Math.max(5, Math.round(xMax / 6))}
          xTickFormat={(v) => `${v}s`}
          showYTickLabels={false}
          xLabel={t("activity.spirometry.timeLabel", {
            defaultValue: "Time (s)",
          })}
        />
        <LockOverlay
          show={!!locks?.breathingTrace}
          to={redemptionCTA?.to}
          onClick={redemptionCTA?.onClick}
          ctaKey="activity.lockedCta"
          ctaDefault={redemptionCTA?.label ?? "Unlock Results"}
          ariaKey="activity.lockedCtaAria"
          ariaDefault={
            redemptionCTA?.ariaLabel ?? "Go to checkout to unlock results"
          }
        />
      </ChartBlock>
    </div>
  );
}
